import type { ICrewTypeSimplified } from '@/modules/CrewType/CrewType'
import type { IDigitalFileSimplified } from '@/modules/DigitalFile/DigitalFile'
import type { IEntitySimplified } from '@/modules/Entity/Entity'
import type { IPersonSuperSimplified } from '@/modules/Person/Person'

//	///////////////////////
//	MARK: CONFIG
export const EMPLOYMENT_CONTRACT_MENU_ITEM = 'role--human-resources--employment-contract'
export const EMPLOYMENT_CONTRACT_ENDPOINT = '/api/employmentContract'
export const EMPLOYMENT_AMENDMENT_ENDPOINT = '/api/employmentContractAmendment'
export const EMPLOYMENT_CONTRACT_LIST_PATH = '/human-resources/employment-contract'

//	///////////////////////
//	MARK: ENUMS
export enum EEmploymentContractType {
  PERMANENT = 'PERMANENT',
  FIXED_TERM = 'FIXED_TERM',
  STUDENT = 'STUDENT',
  INTERNSHIP = 'INTERNSHIP',
}

export enum EEmploymentContractStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ENDED = 'ENDED',
}

export enum EEmploymentAmendmentSubject {
  PERMANENT_CONVERSION = 'PERMANENT_CONVERSION',
  POSITION_CHANGE = 'POSITION_CHANGE',
  REMUNERATION = 'REMUNERATION',
  INSURANCE = 'INSURANCE',
  TRAINING = 'TRAINING',
  TRAINING_REPAYMENT = 'TRAINING_REPAYMENT',
  COMPANY_CAR = 'COMPANY_CAR',
  OTHER = 'OTHER',
}

export enum EEmploymentAmendmentPayrollStatus {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  DISMISSED = 'DISMISSED',
}

export enum EEmploymentContractGapKind {
  SALARIED = 'SALARIED',
  PROVIDER = 'PROVIDER',
}

//	///////////////////////
//	MARK: TYPES
export interface IEmploymentContract {
  id: number
  person: IPersonSuperSimplified
  employer: IEntitySimplified
  contractType: EEmploymentContractType
  crewType: ICrewTypeSimplified
  jobTitle: string | null
  signedOn: Date | null
  startDate: Date
  endDate: Date | null
  status: EEmploymentContractStatus
  signedDocument: IDigitalFileSimplified | null
  notes: string | null
  endedOn: Date | null
  endReason: string | null
  amended?: boolean | null
  lastUpdated: Date
}

export interface IEmploymentContractCommand {
  id: number
  personId: number | null
  employerId: number | null
  contractType: EEmploymentContractType
  crewTypeId: number | null
  jobTitle: string | null
  signedOn: Date | null
  startDate: Date
  endDate: Date | null
  signedDocumentId: number | null
  notes: string | null
}

export interface IEmploymentContractAmendment {
  id: number
  contractId: number
  person: IPersonSuperSimplified
  amendmentNumber: number
  subjects: EEmploymentAmendmentSubject[]
  signedOn: Date | null
  effectiveFrom: Date
  newCrewType: ICrewTypeSimplified | null
  newJobTitle: string | null
  newEndDate: Date | null
  document: IDigitalFileSimplified | null
  payrollStatus: EEmploymentAmendmentPayrollStatus
  payrollAssignmentId: number | null
  notes: string | null
  lastUpdated: Date
}

export interface IEmploymentContractAmendmentCommand {
  id: number
  contractId: number
  subjects: EEmploymentAmendmentSubject[]
  signedOn: Date | null
  effectiveFrom: Date
  newCrewTypeId: number | null
  newJobTitle: string | null
  newEndDate: Date | null
  documentId: number | null
  notes: string | null
}

export interface IEmploymentAmendmentPayrollTerms {
  monthlyRate: number
  monthlyVariableWage: number
  forcedSeniority: number
  endingDate: Date | null
  acertaContractNumber: number | null
  dailyCarBudget: number | null
  dailyCarImpactOnWage: number | null
}

export interface IEmploymentAmendmentPayrollProposal extends IEmploymentAmendmentPayrollTerms {
  amendmentId: number
  effectiveFrom: Date
  crewType: ICrewTypeSimplified
  currentLabel: string | null
  currentMonthlyRate: number | null
  currentMonthlyVariableWage: number | null
  gridIncomplete: boolean
  blocker: string | null
}

export interface IEmploymentContractGap {
  id: number
  person: IPersonSuperSimplified
  crewType: ICrewTypeSimplified
  kind: EEmploymentContractGapKind
  since: Date
}

//	///////////////////////
//	MARK: HELPERS
export const employmentStatusColor = (
  status: EEmploymentContractStatus,
): 'default' | 'success' | 'info' => {
  switch (status) {
    case EEmploymentContractStatus.ACTIVE:
      return 'success'
    case EEmploymentContractStatus.DRAFT:
      return 'info'
    default:
      return 'default'
  }
}

export const employmentContractPath = (contractId: number) =>
  `${EMPLOYMENT_CONTRACT_LIST_PATH}/${contractId.toString()}`

export const employmentPersonPath = (personId: number) =>
  `${EMPLOYMENT_CONTRACT_LIST_PATH}/person/${personId.toString()}`

export const newEmploymentContractPath = (personId: number) =>
  `${EMPLOYMENT_CONTRACT_LIST_PATH}/new?person=${personId.toString()}`
